import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { HeartHandshake, Lock, Share2, ArrowRight, BadgeCheck, Sparkles, Eye, EyeOff } from 'lucide-react';

const PERSONALIZE = [
  'Your name & a personal link',
  'A profile photo or avatar',
  'A short introduction',
  'Reasons to smile',
  'Messages from friends',
  'Favorite memories & meaningful notes',
];

export default function CreateAnchor() {
  const { user } = useAuth();
  const CreateBtn = ({ className }) =>
    user
      ? <Link to="/dashboard"><Button data-testid="create-anchor-cta" className={className}>Create your Anchor <ArrowRight className="w-4 h-4" /></Button></Link>
      : <Button data-testid="create-anchor-cta" onClick={() => (window.location.href = `${window.location.origin}/`)} className={className}>Create your Anchor <ArrowRight className="w-4 h-4" /></Button>;

  return (
    <section id="create-anchor" className="py-24 relative overflow-hidden" data-testid="create-anchor-section">
      <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-mimosa-soft/60 blur-3xl -z-10" />
      <div className="absolute -bottom-32 right-0 w-[380px] h-[380px] rounded-full bg-willow/10 blur-3xl -z-10" />
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <p className="inline-flex items-center gap-2 rounded-full border border-willow/15 bg-white/70 px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] text-willow-dark">
            <HeartHandshake className="w-3 h-3 text-orange" /> Create Your Anchor
          </p>
          <h2 className="font-serif text-4xl md:text-6xl text-willow-dark font-light mt-6 leading-[1.05] tracking-tight">
            Create your own <em className="text-orange not-italic">Anchor</em>.
          </h2>
          <p className="mt-5 text-lg text-[#535E56] leading-relaxed">
            A private space for the things you want to remember, the words you need to hear, and the
            people who want you to know you're not alone.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 items-center mt-16">
          {/* LEFT — the idea + personalization + CTAs */}
          <div>
            <div className="rounded-3xl bg-white/70 border border-willow/10 p-7">
              <p className="text-xs uppercase tracking-[0.28em] text-willow-light">Someone can leave you a message</p>
              <p className="mt-3 text-[#3d463f] leading-relaxed">
                A friend, partner, family member, or anyone you trust can send a few words — encouragement,
                a memory, a reason to smile, or simply a reminder that someone is thinking of you.
              </p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div className="rounded-2xl bg-cloud-subtle p-4">
                  <div className="flex items-center gap-2 text-willow-dark text-sm font-medium"><EyeOff className="w-4 h-4" /> Keep it private</div>
                  <p className="mt-1 text-xs text-willow-light">Save it inside your own Anchor, just for you.</p>
                </div>
                <div className="rounded-2xl bg-mimosa-soft p-4">
                  <div className="flex items-center gap-2 text-willow-dark text-sm font-medium"><Eye className="w-4 h-4" /> Share to your Anchor</div>
                  <p className="mt-1 text-xs text-willow-dark/70">Choose to let it appear on your page — your call.</p>
                </div>
              </div>
              <p className="mt-4 flex items-center gap-2 text-sm text-willow font-medium">
                <Lock className="w-4 h-4" /> You decide what stays private and what you share.
              </p>
            </div>

            <div className="mt-6 rounded-3xl bg-white/70 border border-willow/10 p-7">
              <p className="text-xs uppercase tracking-[0.28em] text-willow-light">Make it yours</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {PERSONALIZE.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-[#3d463f]">
                    <Sparkles className="w-4 h-4 text-orange shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <CreateBtn className="h-13 rounded-full bg-willow hover:bg-willow-dark text-cloud px-7 text-sm gap-2" />
              <Link to="/with/ayeshauy">
                <Button data-testid="visit-ayesha-cta-left" variant="outline" className="h-13 rounded-full border-willow/20 hover:bg-mimosa-soft px-7 text-sm gap-2">
                  Visit Ayesha's Anchor
                </Button>
              </Link>
            </div>

            <div className="mt-6 flex items-center gap-2 text-xs text-willow-light">
              <Share2 className="w-4 h-4" /> Create your Anchor → make it yours → share your link → keep the words that matter.
            </div>
          </div>

          {/* RIGHT — founder example / social proof */}
          <div>
            <Link
              to="/with/ayeshauy"
              data-testid="ayesha-anchor-card"
              className="block rounded-[28px] bg-white border border-willow/10 shadow-[0_20px_60px_-30px_rgba(76,93,75,0.5)] p-7 hover:shadow-[0_28px_70px_-28px_rgba(76,93,75,0.6)] transition"
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-mimosa to-orange flex items-center justify-center text-cloud font-serif text-2xl">A</div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-serif text-2xl text-willow-dark">Ayesha's Anchor</h3>
                    <span className="inline-flex items-center gap-0.5 bg-mimosa-soft text-willow-dark rounded-full px-2 py-0.5 text-[10px] font-semibold"><BadgeCheck className="w-3 h-3" /> Founder</span>
                  </div>
                  <p className="text-xs text-willow-light font-mono">pocketanchor.tech/with/ayeshauy</p>
                </div>
              </div>

              <p className="mt-5 text-sm text-[#535E56] leading-relaxed">
                See how an Anchor can become a personal space for encouragement, memories, and messages from
                people who care.
              </p>

              <div className="mt-5 space-y-3">
                <div className="rounded-2xl bg-cloud-subtle p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-[#A8410F]">A reason to smile</span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-willow-light"><Eye className="w-3 h-3" /> Shared</span>
                  </div>
                  <p className="mt-1.5 text-[15px] text-willow-dark italic">"The world is softer with you in it. Keep going."</p>
                  <p className="mt-1 text-xs text-willow-light">— a kindred soul</p>
                </div>
                <div className="rounded-2xl bg-cloud-subtle p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-[#A8410F]">A favorite memory</span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-willow-light"><EyeOff className="w-3 h-3" /> Private</span>
                  </div>
                  <p className="mt-1.5 text-[15px] text-willow-dark italic">"Our night train to Lisbon. I still hear us laughing."</p>
                  <p className="mt-1 text-xs text-willow-light">— Best friend</p>
                </div>
              </div>

              <div className="mt-6">
                <span className="inline-flex items-center justify-center gap-2 w-full h-12 rounded-full bg-willow text-cloud text-sm font-medium">
                  Visit Ayesha's Anchor <ArrowRight className="w-4 h-4" />
                </span>
              </div>
              <p className="mt-3 text-center text-[11px] text-willow-light">A read-only example — you can't leave a message here.</p>
            </Link>
          </div>
        </div>

        <p className="mt-16 text-center font-serif italic text-willow-light max-w-2xl mx-auto">
          Sometimes a few words from someone who cares can mean more than they realize.
          Give those words a place to stay.
        </p>
      </div>
    </section>
  );
}
